import {
	ChangeDetectionStrategy,
	Component,
	EventEmitter,
	OnInit,
	Output,
	ViewChild,
} from '@angular/core';
import { combineAll, DcBaseComponent } from '@datachain/ui-sdk/common';
import { DxDataGridComponent } from 'devextreme-angular';
import CustomStore from 'devextreme/data/custom_store';
import { OptionChangedEvent } from 'devextreme/ui/accordion';
import { ContentReadyEvent, RowClickEvent } from 'devextreme/ui/data_grid';
import { Map } from 'immutable';
import {
	BehaviorSubject,
	debounceTime,
	firstValueFrom,
	map,
	Observable,
	ReplaySubject,
	takeUntil,
	tap,
} from 'rxjs';

import {
	DateFilterOperations,
	DEFAULT_PAGE_INDEX,
	DEFAULT_PAGE_SIZES,
	DxFilterField,
	DxSortField,
	StringFilterOperations,
} from '../../../core/dx-types';
import { DxGridUtil } from '../../../core/utils/dx-grid.util';
import { DcIcons } from '../../app-dc.icons';
import { DcLoaders } from '../../app-loaders';
import { DxGridProvider } from './dx-grid.provider';
import { MemberItemEntity, SelectedMember } from './member-item.entity';
import { MemberListEntity } from './member-list.entity';

export type MemberListGridOptions = {
	page: number;
	searchTerm: string;
	filterFields: Array<DxFilterField>;
	sortFields: Array<DxSortField>;
};

@Component({
	selector: 'app-member-list',
	templateUrl: './app-member-list.component.html',
	styleUrls: ['./app-member-list.component.scss'],
	inputs: ['memberList', 'initialSelectedMembers'],
	exportAs: 'MembersGrid',
	providers: [DxGridProvider],
	changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AppMemberListComponent extends DcBaseComponent implements OnInit {
	protected readonly DcLoaders = DcLoaders;
	protected readonly DcIcons = DcIcons;
	protected readonly allowedPageSizes = [DEFAULT_PAGE_SIZES[0]];
	protected readonly StringFilterOperations = StringFilterOperations;
	protected readonly DateFilterOperations = DateFilterOperations;
	public dataSource: CustomStore;

	@Output()
	public optionChanged = new EventEmitter<MemberListGridOptions>();

	@Output()
	public selectItems = new EventEmitter<Array<SelectedMember>>();

	public vo$: Observable<{
		memberList: MemberListEntity;
	}>;

	@ViewChild(DxDataGridComponent)
	public grid: DxDataGridComponent | null = null;

	private readonly filterPanelEnabled$ = new BehaviorSubject<boolean>(true);
	private readonly sortFields$ = new BehaviorSubject<Array<DxSortField>>([]);
	private readonly searchMember$ = new BehaviorSubject<string>('');
	private readonly page$ = new BehaviorSubject<number>(0);
	private readonly filterFields$ = new BehaviorSubject<Array<DxFilterField>>(
		[]
	);

	private readonly memberList$ = new ReplaySubject<MemberListEntity>(1);

	private readonly manualTrigger$ = new ReplaySubject<string>(1);

	private readonly initialSelectedMembers$ = new BehaviorSubject<
		Map<number, SelectedMember>
	>(Map());

	public constructor(private readonly dxGridProvider: DxGridProvider) {
		super();
		this.cmpId = 'app-member-list';
		this.dataSource = this.dxGridProvider.createCustomStore();

		const members$ = this.toObservable<MemberListEntity>('memberList').pipe(
			map((list) => {
				this.grid?.instance.refresh(true);
				this.dxGridProvider.updateData(list);
				this.memberList$.next(list);
				return list;
			})
		);

		this.vo$ = combineAll({
			memberList: members$,
		});
	}

	public ngOnInit(): void {
		super.ngOnInit();
		this.manualTrigger$.next('init');
		this.handleSubscription();
	}

	public async onGridOptionChange(event: OptionChangedEvent): Promise<void> {
		if (
			event.name !== 'paging' &&
			event.name !== 'filterValue' &&
			event.name !== 'filterPanel' &&
			event.name !== 'columns'
		) {
			return;
		}
		const { fullName, value } = event;

		if (fullName.includes('pageIndex')) {
			this.page$.next(value);
		} else if (fullName === 'filterValue') {
			const filteredFields = value ? DxGridUtil.parseFilters(value) : [];
			this.filterFields$.next(filteredFields);
			this.page$.next(DEFAULT_PAGE_INDEX);
		} else if (fullName.includes('filterEnabled')) {
			this.filterPanelEnabled$.next(value);
		} else if (fullName.includes('columns') && fullName.endsWith('sortOrder')) {
			let sortedFields: Array<DxSortField> = [];
			if (value) {
				const memberList = await firstValueFrom(this.memberList$);
				sortedFields = DxGridUtil.generateSortFieldsV1<MemberItemEntity>(
					memberList.availableColumns,
					fullName,
					value
				);
			}
			this.sortFields$.next(sortedFields);
		}
	}

	public search(searchTerm: string): void {
		if (this.grid === null) {
			return;
		}
		const searchWord = searchTerm.trim();
		this.searchMember$.next(searchWord);
	}

	public async onSelectionChanged(): Promise<void> {
		const selected = this.getSelectedRows();

		const initialSelectedMembers = await firstValueFrom(
			this.initialSelectedMembers$
		);

		const allSelected: Array<SelectedMember> =
			initialSelectedMembers.size === 0
				? selected
				: [...selected, ...initialSelectedMembers.toList().toArray()];

		this.selectItems.emit(allSelected);
	}

	public async onRowClick(e: RowClickEvent): Promise<void> {
		const grid = e.component;

		if (grid.isRowSelected(e.key)) {
			await grid.deselectRows([e.key]);
		} else {
			await grid.selectRows([e.key], true);
		}
	}

	public async onContentReady(event: ContentReadyEvent): Promise<void> {
		const initialSelectedMembers = await firstValueFrom(
			this.initialSelectedMembers$
		);

		if (initialSelectedMembers.size === 0) {
			return;
		}

		const currentVisibleRowIds = event.component
			.getVisibleRows()
			.map<number>((r) => r.key);

		const inSelectedMembersInCurrentPage = initialSelectedMembers.filter(
			(key) => currentVisibleRowIds.includes(key.id)
		);

		if (inSelectedMembersInCurrentPage.size === 0) {
			return;
		}

		const updated = initialSelectedMembers.filter(
			(key) => !currentVisibleRowIds.includes(key.id)
		);

		this.initialSelectedMembers$.next(updated);

		this.grid?.instance.selectRows(
			inSelectedMembersInCurrentPage.keySeq().toArray(),
			true
		);
	}

	public refresh(): void {
		this.manualTrigger$.next('refresh');
	}

	public resetGrid(): void {
		if (this.grid) {
			this.grid.instance.clearFilter();
			this.grid.instance.clearSorting();
			this.grid.instance.pageIndex(DEFAULT_PAGE_INDEX);
			this.manualTrigger$.next('reset');
		}
	}

	public async removeSelectedMember(id: number): Promise<void> {
		const isSelected = this.grid?.instance.isRowSelected(id);
		if (isSelected) {
			this.grid?.instance.deselectRows([id]);
			return;
		}
		const initialSelectedMembers = await firstValueFrom(
			this.initialSelectedMembers$
		);

		const updateInitialMembers = initialSelectedMembers.delete(id);
		this.initialSelectedMembers$.next(updateInitialMembers);

		const selected = this.getSelectedRows();

		this.selectItems.emit([
			...selected,
			...updateInitialMembers.toList().toArray(),
		]);
	}

	private getSelectedRows(): Array<SelectedMember> {
		const selectedRows =
			this.grid?.instance.getSelectedRowsData() as Array<MemberItemEntity>;

		return selectedRows.map<SelectedMember>((row) => ({
			id: row.id,
			name: row.identifyName,
			isGroup: row.isGroup,
			avatar: row.avatar,
		}));
	}

	private handleSubscription(): void {
		this.toObservable<Map<number, SelectedMember>>('initialSelectedMembers')
			.pipe(
				tap((members) => {
					this.initialSelectedMembers$.next(members);
				})
			)
			.subscribe();

		combineAll({
			manualTrigger: this.manualTrigger$,
			page: this.page$,
			filterFields: this.filterFields$,
			sortFields: this.sortFields$,
			searchTerm: this.searchMember$.pipe(
				debounceTime(300),
				tap((searchWord) => {
					this.grid?.instance.searchByText(searchWord);
				})
			),
			filterPanelEnabled: this.filterPanelEnabled$,
		})
			.pipe(
				takeUntil(this.onDestroy$),
				debounceTime(500),
				tap(
					({
						page,
						filterFields,
						sortFields,
						searchTerm,
						filterPanelEnabled,
					}) => {
						this.optionChanged.emit({
							page,
							searchTerm,
							filterFields: filterPanelEnabled
								? filterFields
								: new Array<DxFilterField<MemberItemEntity>>(),
							sortFields,
						});
					}
				)
			)
			.subscribe();
	}
}
